/* THE RACKET HOUSE — storefront interactions (hero, rails, tabs, quick add, reveal) */
(function () {
  'use strict';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- HERO ---------- */
  function initHero(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const slides = [...root.querySelectorAll('.trh-hero__slide')];
    const tabs = [...root.querySelectorAll('.trh-hero__tab')];
    if (slides.length < 2) return;
    const dur = (parseFloat(root.dataset.autoplay) || 6) * 1000;
    root.style.setProperty('--dur', dur / 1000 + 's');
    let i = 0, timer = null, paused = false, remaining = dur, started = 0;

    function go(n, user) {
      slides[i].classList.remove('is-active');
      tabs[i] && tabs[i].classList.remove('is-active');
      i = (n + slides.length) % slides.length;
      slides[i].classList.add('is-active');
      if (tabs[i]) {
        const bar = tabs[i].querySelector('i');
        tabs[i].classList.remove('is-active');
        if (bar) { bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = ''; }
        tabs[i].classList.add('is-active');
      }
      remaining = dur;
      schedule();
    }
    function schedule() {
      clearTimeout(timer);
      if (paused || reduce) return;
      started = Date.now();
      timer = setTimeout(() => go(i + 1), remaining);
    }
    function pause() {
      if (paused) return;
      paused = true; root.classList.add('is-paused');
      clearTimeout(timer); remaining -= Date.now() - started;
    }
    function resume() {
      if (!paused) return;
      paused = false; root.classList.remove('is-paused');
      schedule();
    }
    tabs.forEach((t, n) => t.addEventListener('click', () => go(n, true)));
    root.querySelector('[data-hero-prev]')?.addEventListener('click', () => go(i - 1, true));
    root.querySelector('[data-hero-next]')?.addEventListener('click', () => go(i + 1, true));
    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', resume);
    document.addEventListener('visibilitychange', () => (document.hidden ? pause() : resume()));
    let x0 = null;
    root.addEventListener('pointerdown', (e) => { x0 = e.clientX; }, { passive: true });
    root.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0; x0 = null;
      if (Math.abs(dx) > 50) go(i + (dx < 0 ? 1 : -1), true);
    });
    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') go(i + 1, true);
      if (e.key === 'ArrowLeft') go(i - 1, true);
    });
    schedule();
  }

  /* ---------- RAILS ---------- */
  function initRail(rail) {
    if (rail.dataset.ready) return;
    rail.dataset.ready = '1';
    const track = rail.querySelector('.trh-rail__track');
    const prev = rail.querySelector('[data-rail-prev]');
    const next = rail.querySelector('[data-rail-next]');
    const bar = rail.querySelector('.trh-rail__bar i');
    if (!track) return;
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const ratio = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
      if (bar) {
        bar.style.setProperty('--w', Math.min(100, ratio * 100) + '%');
        const p = max > 0 ? track.scrollLeft / max : 0;
        bar.style.setProperty('--x', p * (1 / Math.max(ratio, 0.01) - 1) * 100 + '%');
      }
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft >= max - 4;
      const foot = rail.querySelector('.trh-rail__foot');
      if (foot) foot.style.visibility = max <= 4 ? 'hidden' : '';
    };
    const step = () => track.clientWidth * 0.85;
    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
    rail._update = update;
  }

  /* ---------- TABS ---------- */
  function initTabs(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const btns = [...root.querySelectorAll('.trh-tabs__btn')];
    const panels = [...root.querySelectorAll('.trh-tabs__panel')];
    const more = root.querySelector('[data-tabs-more]');
    btns.forEach((b, n) => {
      b.addEventListener('click', () => {
        btns.forEach((x, k) => x.setAttribute('aria-selected', k === n ? 'true' : 'false'));
        panels.forEach((p, k) => (p.hidden = k !== n));
        const r = panels[n].querySelector('[data-trh-rail]');
        if (r && r._update) r._update();
        if (more && b.dataset.url) { more.href = b.dataset.url; more.querySelector('span').textContent = b.dataset.more || 'View all'; }
      });
      b.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const t = btns[(n + (e.key === 'ArrowRight' ? 1 : -1) + btns.length) % btns.length];
        t.focus(); t.click();
      });
    });
  }

  /* ---------- QUICK ADD + UPSELL TOAST ---------- */
  let toast;
  function money(cents) {
    try { return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100); } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function getToast() {
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'trh-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML =
      '<div class="trh-toast__top"><img alt=""><div><div class="trh-toast__label">Added to your bag</div><div class="trh-toast__name"></div></div><button class="trh-toast__x" aria-label="Close">&times;</button></div>' +
      '<div class="trh-toast__ups"></div>' +
      '<div class="trh-toast__btns"><a class="trh-btn trh-btn--line" href="/cart" style="--fg:#fff;--bd:rgba(255,255,255,.5)">View bag <span class="trh-toast__count"></span></a><a class="trh-btn trh-btn--gold" href="/checkout">Checkout</a></div>';
    document.body.appendChild(toast);
    toast.querySelector('.trh-toast__x').addEventListener('click', () => toast.classList.remove('is-open'));
    return toast;
  }
  const UPSELLS = [
    'Playing tonight? Grab <a href="/collections/balls">fresh balls</a> on the way out.',
    'Pros re-grip every few weeks. <a href="/collections/accessories/overgrip">Add overgrips</a>.',
    'Racket feeling dead? <a href="/products/stringing-service">Book a restring</a>.',
    'Keep it all together: <a href="/collections/bags">shop bags</a>.'
  ];
  async function refreshCount() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      document.querySelectorAll('.trh-toast__count').forEach((n) => (n.textContent = '(' + cart.item_count + ')'));
      document.querySelectorAll('cart-bubble, .cart-bubble, [data-cart-count], .header-actions__cart-icon .cart-bubble__text-count, .cart-count').forEach((el) => {
        const t = el.querySelector('.cart-bubble__text-count, [aria-hidden]') || el;
        if (t && /^\d*$/.test((t.textContent || '').trim())) t.textContent = cart.item_count;
        el.classList && el.classList.remove('visually-hidden', 'hidden');
      });
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
    } catch (e) { /* noop */ }
  }
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-trh-add]');
    if (!btn) return;
    e.preventDefault();
    const label = btn.innerHTML;
    btn.classList.add('is-loading');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.trhAdd), quantity: 1 }] })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add');
      const item = (data.items && data.items[0]) || data;
      const t = getToast();
      t.querySelector('img').src = btn.dataset.img || item.image || '';
      t.querySelector('.trh-toast__name').textContent = (btn.dataset.title || item.product_title || '') + ' — ' + money(item.final_price || item.price || 0);
      t.querySelector('.trh-toast__ups').innerHTML = UPSELLS[Math.floor(Math.random() * UPSELLS.length)];
      t.classList.add('is-open');
      clearTimeout(t._h); t._h = setTimeout(() => t.classList.remove('is-open'), 7000);
      btn.classList.add('is-added');
      if (!btn.classList.contains('trh-mini__add')) btn.innerHTML = 'Added ✓';
      setTimeout(() => { btn.classList.remove('is-added'); btn.innerHTML = label; }, 2200);
      refreshCount();
    } catch (err) {
      btn.innerHTML = 'Sold out';
      setTimeout(() => (btn.innerHTML = label), 2200);
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* ---------- REVEAL + COUNT UP ---------- */
  let io;
  function initReveal(scope) {
    const els = scope.querySelectorAll('[data-trh-reveal]:not(.is-in)');
    if (!('IntersectionObserver' in window) || reduce) { els.forEach((el) => el.classList.add('is-in')); return; }
    io = io || new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        en.target.querySelectorAll('[data-count]').forEach(countUp);
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  }
  function countUp(el) {
    const end = parseFloat(el.dataset.count); if (!end) return;
    const t0 = performance.now(), d = 1400;
    const fmt = (v) => Math.round(v).toLocaleString('en-CA');
    const tick = (t) => { const p = Math.min(1, (t - t0) / d); el.textContent = fmt(end * (1 - Math.pow(1 - p, 3))); if (p < 1) requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  }

  function init(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-trh-hero]').forEach(initHero);
    scope.querySelectorAll('[data-trh-rail]').forEach(initRail);
    scope.querySelectorAll('[data-trh-tabs]').forEach(initTabs);
    initReveal(scope);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
